import { fabric } from 'fabric';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import {
  findApplianceObject,
  findShapeContainingAppliance,
  isApplianceAlreadySelected,
  moveApplianceToCanvasAndSelect,
  setupApplianceMovement,
} from './useMouseDown.utils';

import {
  CANVAS_CURSOR,
  CANVAS_OBJECT_TYPE_PREFIX,
  CANVAS_OBJECT_TYPE_SUFFIX,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { DRAGGING_STATE } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import {
  getOutlinePointsFromShape,
  isPointInsidePolygon,
} from '~/core/helpers';
import {
  CornerInfo,
  CornerRadiusModel,
  DiagonalCornerModel,
  DrawingShape,
} from '~/core/models';
import { CustomSideModel } from '~/core/models/predefined-custom-side.model';
import { hideAllLabels } from '~/core/utils';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useCanvasSelectionHandler } from '~/legacy/easy-estimator-page/context/fabricCanvasContext/hooks';
import { useAppDispatch } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { updateSelectedDrawingTab } from '~/redux/slices/shape';

export const useMouseDown = (canvas: fabric.Canvas) => {
  const dispatch = useAppDispatch();

  const {
    getters: { activeDrawingApplianceId },
    handlers: {
      updateActiveDrawingAppliance,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
    },
  } = useDrawingShapeContext();

  const {
    draggingStateRef,
    drawingShapesRef,
    isShapeLogicRef,
    movingItemRef,
    lastPositionRef,
    shapeStartDragRef,
  } = useFabricCanvasContext();
  const { handleCornerSelection, handleEdgeSelection } =
    useCanvasSelectionHandler();

  const handleApplianceSelection = useCallback(
    (targetName: string, pointer: Vec2) => {
      const shape = findShapeContainingAppliance(
        drawingShapesRef.current,
        targetName
      );

      if (shape) {
        const searchResult = findApplianceObject(canvas, shape, targetName);

        // Already selected by handleApplianceInteraction - just set up for continued movement
        if (
          isApplianceAlreadySelected(
            searchResult.applianceOnCanvas,
            activeDrawingApplianceId,
            targetName
          )
        ) {
          setupApplianceMovement({
            movingItemRef,
            shapeStartDragRef,
            targetName,
            pointer,
          });
          canvas.moveCursor = CANVAS_CURSOR.FREE_DRAWING.POINTER;

          return;
        }

        // Different appliance was selected - deselect it (but don't move back to group)
        // NOTE: Do NOT call addWithUpdate() - it corrupts positions by recalculating
        // relative to the group center. Appliances stay on canvas root.
        if (
          activeDrawingApplianceId &&
          activeDrawingApplianceId !== targetName
        ) {
          canvas.discardActiveObject();
        }

        // Move new appliance to canvas and select
        moveApplianceToCanvasAndSelect(canvas, shape, searchResult);
      }

      // Update state and refs (always runs, even if shape not found)
      updateActiveDrawingAppliance(targetName);
      setupApplianceMovement({
        movingItemRef,
        shapeStartDragRef,
        targetName,
        pointer,
      });
      // Use renderAll(), NOT dispatch(updateNeedRedrawCanvas(true)) - avoids race condition
      canvas.renderAll();
      canvas.moveCursor = CANVAS_CURSOR.FREE_DRAWING.POINTER;
    },
    [
      updateActiveDrawingAppliance,
      movingItemRef,
      shapeStartDragRef,
      canvas,
      drawingShapesRef,
      activeDrawingApplianceId,
    ]
  );

  const handleCircleClick = useCallback(
    (target: fabric.Object) => {
      canvas.remove(target);

      const data = target.data;

      if (data.cornerId) {
        const corner: CornerInfo =
          data.parentDrawingShape.cornerMap[data.cornerId];
        const selectedOption: CornerRadiusModel | DiagonalCornerModel =
          data.selectedOption;

        handleCornerSelection(corner, selectedOption);
      } else {
        const selectedOption: CustomSideModel = data.selectedOption;
        const shape: DrawingShape = data.parentDrawingShape;
        const edge = shape.drawingEdges.find(({ id }) => id === data.edgeId);

        if (edge) {
          handleEdgeSelection(edge, shape, selectedOption);
        }
      }

      updateDrawingShapes([...drawingShapesRef.current]);
      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      canvas,
      dispatch,
      drawingShapesRef,
      handleCornerSelection,
      handleEdgeSelection,
      updateDrawingShapes,
    ]
  );

  const removeShapeObjects = useCallback(
    (shape: DrawingShape): void => {
      const withLabel = (prefix: string) => `${prefix}_${shape.label}`;

      // NOTE: Do NOT remove appliances here - they should persist through shape interactions
      // and maintain their positions. Appliances are managed separately by addAppliancesToCanvas.
      (
        [
          [withLabel(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL), true],
          [withLabel(CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME), true],
          [CANVAS_OBJECT_TYPE_PREFIX.INTERIOR_ARROW, false],
        ] as [string, boolean][]
      ).forEach(([name, isStrict]) =>
        drawingTool.removeObjectByName(canvas, name, isStrict)
      );

      hideAllLabels(shape.editableLabels);
    },
    [canvas]
  );

  const updateShapeState = useCallback(
    (shape: DrawingShape): void => {
      // beginning of moving a shape
      movingItemRef.current = shape;

      drawingTool.removeObjectByName(
        canvas,
        CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
      );
      updateActiveDrawingShapeId(shape.id);
      dispatch(updateNeedRedrawCanvas(true));
      dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
      canvas.moveCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
    },
    [canvas, dispatch, movingItemRef, updateActiveDrawingShapeId]
  );

  const handleShapeInteraction = useCallback(
    (targetName: string, pointer: Vec2) => {
      // Clear any active appliance before shape interaction
      // This ensures appliances maintain their updated position during redraw
      // (mirrors the pattern in startDragging for consistent behavior)
      if (activeDrawingApplianceId) {
        updateActiveDrawingAppliance(undefined);
      }

      const shape = drawingShapesRef.current.find(
        (dShape) =>
          targetName ===
          dShape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP
      );

      if (!shape) {
        return;
      }

      const shapeOutlinePoints = getOutlinePointsFromShape(shape);

      shapeStartDragRef.current = pointer;
      removeShapeObjects(shape);

      hideAllLabels(shape.editableLabels);

      if (isPointInsidePolygon(shapeOutlinePoints, pointer)) {
        movingItemRef.current = shape;
        updateShapeState(shape);
      } else if (canvas.freeDrawingCursor === CANVAS_CURSOR.FREE_DRAWING.DRAW) {
        // if we are free drawing, do this (better way to test this?)
        dispatch(updateNeedRedrawCanvas(true));
        movingItemRef.current = undefined;
      } else if (isShapeLogicRef.current) {
        movingItemRef.current = shape;
        drawingTool.removeObjectByName(
          canvas,
          CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
        );
      }
    },
    [
      activeDrawingApplianceId,
      updateActiveDrawingAppliance,
      drawingShapesRef,
      shapeStartDragRef,
      removeShapeObjects,
      canvas,
      isShapeLogicRef,
      movingItemRef,
      updateShapeState,
      dispatch,
    ]
  );

  const startDragging = useCallback(
    (e: MouseEvent) => {
      // If there's an active appliance, just deselect it - no redraw needed.
      // Appliances stay on canvas root (never go back to fabricGroup),
      // so we just need to clear the selection state.
      // canvas.discardActiveObject() handles the visual deselection.
      if (activeDrawingApplianceId) {
        updateActiveDrawingAppliance(undefined);
        // DO NOT trigger redraw here - it would read from React state
        // before syncShapesToContext() has committed, causing position snap-back
      }

      canvas.discardActiveObject();
      draggingStateRef.current = DRAGGING_STATE.DRAGGING;
      lastPositionRef.current = { x: e.clientX, y: e.clientY };
    },
    [
      canvas,
      draggingStateRef,
      lastPositionRef,
      activeDrawingApplianceId,
      updateActiveDrawingAppliance,
    ]
  );

  return {
    canvas,
    handleApplianceSelection,
    handleCircleClick,
    handleShapeInteraction,
    startDragging,
  };
};
